// src/pages/ScoreEntryPage.jsx — host admin: live API pull + manual fallback.
// Stats write to Firebase in real-time as they're entered/updated.
// All users see live score updates via onValue subscriptions in SeasonPage.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { auth, db } from "../firebase";
import { ref, onValue, get, update } from "firebase/database";
import { calcPlayerPoints } from "../services/scoring";
import { LiveScorePoller, matchPlayerName, fetchScorecard, parseScorecardToStats, fetchAllIplMatches } from "../services/liveScores";
import { IPL_2026_MATCHES, gameRoundToFantasyWeek } from "../services/iplSchedule";
import { players as ALL_PLAYERS } from "../players";

// Static lookup: player id (string) → IPL team abbreviation e.g. "RCB"
// Built from players.js which is the authoritative source — never rely on
// room.players.team from Firebase, which may be missing or stale.
const PLAYER_IPL_TEAM = {};
ALL_PLAYERS.forEach(p => { PLAYER_IPL_TEAM[String(p.id)] = p.team || ""; });

const DEFAULT_RULES = {
  batting: {
    run: 1, fourBonus: 1, sixBonus: 2,
    duckPenalty: -2, duckPenaltyOnlyIfOut: true,
    milestones: [
      { minRuns: 100, points: 16 }, { minRuns: 75, points: 12 },
      { minRuns: 50,  points: 8  }, { minRuns: 30, points: 4  },
    ],
    strikeRate: { minBalls: 10, brackets: [
      { min: 170,                  points: 6  },
      { min: 150, max: 169.99,     points: 4  },
      { min: 130, max: 149.99,     points: 2  },
      { min: 60,  max: 70.99,      points: -2 },
      {           max: 59.99,      points: -4 },
    ]},
  },
  bowling: {
    wicket: 25, maiden: 8, dotBall: 1,
    wicketMilestones: [
      { minWkts: 5, points: 16 }, { minWkts: 4, points: 12 },
      { minWkts: 3, points: 8  }, { minWkts: 2, points: 4  },
    ],
    economy: { minOvers: 2, brackets: [
      {           max: 4.99,  points: 6  },
      { min: 5,   max: 5.99,  points: 4  },
      { min: 6,   max: 7,     points: 2  },
      { min: 10,  max: 11,    points: -2 },
      { min: 11,              points: -4 },
    ]},
  },
  fielding: { catch: 8, stumping: 12, runoutDirect: 12, runoutAssist: 6 },
  caps: { minPoints: -10 },
};

const EMPTY_STAT = () => ({
  bat:  { runs: "", balls: "", fours: "", sixes: "", out: false },
  bowl: { overs: "", runs: "", wkts: "", maidens: "", dots: "" },
  fld:  { catches: "", stumpings: "", runoutDirect: "", runoutAssist: "" },
});

function n(v) { const x = parseFloat(v); return isNaN(x) ? 0 : x; }

// When parseScorecardToStats returns multiple entries that matchPlayerName maps
// to the SAME player ID, the second write would silently overwrite the first.
// Example: "D Padikkal" (batting entry, runs=50) and "padikkal" (fielding-credit
// entry extracted from a dismissal string, runs=0 but catches=2) both resolve to
// Padikkal's player ID.  Without the merge, the catches entry lands last and
// wipes the batting stats → 16 pts instead of the correct 73.
//
// Fix: merge bat/bowl by taking max of each field (fielding-credit entries are all
// zeros); merge fld by summing (a player can earn credits from multiple dismissals).
function mergeMatchStat(a, b) {
  return {
    bat: {
      runs:  Math.max(a.bat.runs  ?? 0, b.bat.runs  ?? 0),
      balls: Math.max(a.bat.balls ?? 0, b.bat.balls ?? 0),
      fours: Math.max(a.bat.fours ?? 0, b.bat.fours ?? 0),
      sixes: Math.max(a.bat.sixes ?? 0, b.bat.sixes ?? 0),
      out:   !!(a.bat.out || b.bat.out),
    },
    bowl: {
      overs:   Math.max(a.bowl.overs   ?? 0, b.bowl.overs   ?? 0),
      runs:    Math.max(a.bowl.runs    ?? 0, b.bowl.runs    ?? 0),
      wkts:    Math.max(a.bowl.wkts    ?? 0, b.bowl.wkts    ?? 0),
      maidens: Math.max(a.bowl.maidens ?? 0, b.bowl.maidens ?? 0),
      dots:    Math.max(a.bowl.dots    ?? 0, b.bowl.dots    ?? 0),
    },
    fld: {
      catches:      (a.fld.catches      ?? 0) + (b.fld.catches      ?? 0),
      stumpings:    (a.fld.stumpings    ?? 0) + (b.fld.stumpings    ?? 0),
      runoutDirect: (a.fld.runoutDirect ?? 0) + (b.fld.runoutDirect ?? 0),
      runoutAssist: (a.fld.runoutAssist ?? 0) + (b.fld.runoutAssist ?? 0),
    },
  };
}

function StatInput({ value, onChange, width = 52 }) {
  return (
    <input type="number" value={value} onChange={e => onChange(e.target.value)} min={0}
      style={{
        width, textAlign: "center", padding: "5px 4px", fontSize: 12,
        background: "var(--bg2)", border: "1px solid var(--border)",
        color: "var(--text)", borderRadius: "var(--radius-sm)",
      }} />
  );
}

// Debounce: save to Firebase 800ms after last keystroke
function useDebounce(fn, delay) {
  const timer = useRef(null);
  return useCallback((...args) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => fn(...args), delay);
  }, [fn, delay]); // eslint-disable-line
}

export function ScoreEntryPage({ leagueCode }) {
  const uid = auth.currentUser?.uid;

  const [league,       setLeague]       = useState(null);
  const [room,         setRoom]         = useState(null);
  const [week,         setWeek]         = useState(1);
  const [iplMatchNum,  setIplMatchNum]  = useState(""); // IPL fixture number (match-based entry)
  const [saving,       setSaving]       = useState(false);
  const [syncProgress, setSyncProgress] = useState(null); // { done, total, msg }
  const [status,       setStatus]       = useState("");
  const [stats,        setStats]        = useState({});
  const [search,       setSearch]       = useState("");

  // Live API state
  const [apiKey,       setApiKey]       = useState(localStorage.getItem("cricket_api_key") || "");
  const [apiKeyInput,  setApiKeyInput]  = useState("");
  const [liveMatches,  setLiveMatches]  = useState([]);
  const [selectedMatch,setSelectedMatch]= useState("");
  const [manualMatchId,setManualMatchId]= useState(""); // fallback: paste Cricbuzz match ID
  const [polling,      setPolling]      = useState(false);
  const [lastPoll,     setLastPoll]     = useState(null);
  const [pollError,    setPollError]    = useState("");
  const [apiStatus,    setApiStatus]    = useState(""); // "pulling" | "live" | "error"
  // Persistent map: fixture match number (string) → Cricbuzz match ID
  // Stored in Firebase so IDs accumulate across sessions and sync runs
  const [matchIdMap,   setMatchIdMap]   = useState({});
  // Bulk sync: user pastes a list of Cricbuzz IDs; we auto-detect fixture per ID
  const [bulkIdText,   setBulkIdText]   = useState("");
  const [bulkResults,  setBulkResults]  = useState([]); // [{id, status, msg}]

  const pollerRef = useRef(null);

  useEffect(() => {
    if (!leagueCode) return;
    const code = leagueCode.toUpperCase();
    const u1 = onValue(ref(db, `leagues/${code}`), s => setLeague(s.val() || null));
    const u2 = onValue(ref(db, `rooms/${code}`),   s => setRoom(s.val() || null));
    return () => { u1(); u2(); };
  }, [leagueCode]);

  useEffect(() => {
    if (league?.season?.week) setWeek(league.season.week);
  }, [league?.season?.week]);

  // Load persisted Cricbuzz match ID map from Firebase
  useEffect(() => {
    if (!leagueCode) return;
    const code = leagueCode.toUpperCase();
    return onValue(ref(db, `leagues/${code}/config/matchIds`), s => {
      setMatchIdMap(s.val() || {});
    });
  }, [leagueCode]);

  const isHost = useMemo(() => {
    if (!uid) return false;
    return room?.hostId === uid || league?.ownerUid === uid;
  }, [uid, room?.hostId, league?.ownerUid]);

  // Resolve the selected IPL fixture (when using match-based entry)
  const iplFixture = useMemo(
    () => IPL_2026_MATCHES.find(m => m.match === Number(iplMatchNum)) || null,
    [iplMatchNum]
  );

  // Load existing stats — in match mode, load each player only from their
  // correct target week.  For same-week matches this is a simple single load.
  // For cross-week matches (e.g. RR homeGame=4 / RCB awayGame=3) we must NOT
  // merge both week-buckets blindly: that would pull a player's Week 3 stats
  // into the editor for a Week 4 match, and the debounced save would then write
  // those Week 3 values into Week 4 — duplicating the score across both weeks.
  useEffect(() => {
    if (!leagueCode) return;
    const code = leagueCode.toUpperCase();
    if (iplFixture) {
      const homeWeek = gameRoundToFantasyWeek(iplFixture.homeGame);
      const awayWeek = gameRoundToFantasyWeek(iplFixture.awayGame);
      const weeks = [...new Set([homeWeek, awayWeek])];
      Promise.all(weeks.map(w => get(ref(db, `leagues/${code}/season/stats/${w}`)))).then(snaps => {
        const byWeek = {};
        weeks.forEach((w, i) => { byWeek[w] = snaps[i].val() || {}; });

        if (homeWeek === awayWeek) {
          // Same-week match — no cross-contamination risk, use the full bucket
          setStats(byWeek[homeWeek]);
        } else {
          // Cross-week match: include each player only from their correct week
          const merged = {};
          // Iterate over all known player IDs and pick from the correct week
          Object.entries(PLAYER_IPL_TEAM).forEach(([pid, team]) => {
            if (team === iplFixture.home && byWeek[homeWeek][pid]) {
              merged[pid] = byWeek[homeWeek][pid];
            } else if (team === iplFixture.away && byWeek[awayWeek][pid]) {
              merged[pid] = byWeek[awayWeek][pid];
            }
          });
          setStats(merged);
        }
      });
    } else {
      get(ref(db, `leagues/${code}/season/stats/${week}`)).then(snap => {
        setStats(snap.exists() ? snap.val() : {});
      });
    }
  }, [leagueCode, week, iplFixture]);

  const allPlayers = useMemo(() => {
    if (!room) return [];
    const pm = room.players || {};
    const teams = room.teams || [];
    return teams.flatMap(t =>
      (t.squadIds || []).map(id => pm[String(id)]).filter(Boolean)
    );
  }, [room]);

  const filtered = useMemo(() => {
    let list = allPlayers;
    // In match mode, only show players from the two IPL teams in the match.
    // Use PLAYER_IPL_TEAM (from players.js) — don't rely on p.team from Firebase
    // which may be absent in older rooms.
    if (iplFixture) {
      list = list.filter(p => {
        const t = PLAYER_IPL_TEAM[String(p.id)] || "";
        return t === iplFixture.home || t === iplFixture.away;
      });
    }
    if (!search.trim()) return list;
    const q = search.toLowerCase();
    return list.filter(p => p.name.toLowerCase().includes(q));
  }, [allPlayers, search, iplFixture]);

  const getStat  = (pid) => stats[String(pid)] || EMPTY_STAT();

  const updateStat = useCallback((pid, section, field, value) => {
    setStats(prev => ({
      ...prev,
      [String(pid)]: {
        ...(prev[String(pid)] || EMPTY_STAT()),
        [section]: { ...(prev[String(pid)]?.[section] || {}), [field]: value },
      },
    }));
    setStatus("");
  }, []);

  // ── Firebase push helpers ──────────────────────────────────────

  // fixtureOverride lets syncFullSeason pass a per-match fixture without
  // changing component state — falls back to the UI-selected iplFixture.
  const pushAllScores = useCallback(async (currentStats, fixtureOverride = null) => {
    if (!leagueCode) return;
    const code = leagueCode.toUpperCase();
    const activeFixture = fixtureOverride ?? iplFixture;

    // Determine which fantasy week each player's stats belong to.
    const getTargetWeek = (pid) => {
      if (!activeFixture) return week;
      const iplTeam = PLAYER_IPL_TEAM[String(pid)] || "";
      if (iplTeam === activeFixture.home) return gameRoundToFantasyWeek(activeFixture.homeGame);
      if (iplTeam === activeFixture.away) return gameRoundToFantasyWeek(activeFixture.awayGame);
      return week;
    };

    // Group stats and points by target week
    const statsByWeek = {};
    const pointsByWeek = {};
    for (const [pid, stat] of Object.entries(currentStats)) {
      // Only process players from the two fixture IPL teams.
      if (activeFixture) {
        const iplTeam = PLAYER_IPL_TEAM[String(pid)] || "";
        if (iplTeam !== activeFixture.home && iplTeam !== activeFixture.away) continue;
      }
      const normalized = {
        bat:  { runs: n(stat.bat?.runs), balls: n(stat.bat?.balls), fours: n(stat.bat?.fours), sixes: n(stat.bat?.sixes), out: !!stat.bat?.out },
        bowl: { overs: n(stat.bowl?.overs), runs: n(stat.bowl?.runs), wkts: n(stat.bowl?.wkts), maidens: n(stat.bowl?.maidens), dots: n(stat.bowl?.dots) },
        fld:  { catches: n(stat.fld?.catches), stumpings: n(stat.fld?.stumpings), runoutDirect: n(stat.fld?.runoutDirect), runoutAssist: n(stat.fld?.runoutAssist) },
      };
      const pts = calcPlayerPoints(normalized, DEFAULT_RULES);
      const w = getTargetWeek(pid);
      if (!statsByWeek[w]) { statsByWeek[w] = {}; pointsByWeek[w] = {}; }
      statsByWeek[w][pid] = stat;
      pointsByWeek[w][pid] = pts;
    }

    const dbUpdates = {};
    for (const [w, wStats] of Object.entries(statsByWeek)) {
      // Merge into existing stats for this week, stripping only the team(s) that
      // *belong to this specific week* in the fixture — NOT both fixture teams.
      //
      // Why the distinction matters for cross-week fixtures:
      //   M11: RCB homeGame=2→Wk2, CSK awayGame=3→Wk3
      //   M16: RR  homeGame=4→Wk4, RCB awayGame=3→Wk3
      //
      // When syncing M11 and writing to Wk3 (CSK data), we must NOT strip RCB from
      // Wk3 — RCB's correct Wk3 stats come from M16 and belong there.
      // Stripping both teams would delete RCB's Wk3 data every time M11 is re-synced.
      //
      // Rule: for week W, only strip the team whose game-round maps to W.
      //   same-week match  → both teams map to W → strip both (no change from before)
      //   cross-week match → only one team maps to W → strip that one only
      const existingSnap = await get(ref(db, `leagues/${code}/season/stats/${w}`));
      const existing = existingSnap.val() || {};

      const teamsForThisWeek = new Set();
      if (activeFixture) {
        if (gameRoundToFantasyWeek(activeFixture.homeGame) === Number(w)) teamsForThisWeek.add(activeFixture.home);
        if (gameRoundToFantasyWeek(activeFixture.awayGame) === Number(w)) teamsForThisWeek.add(activeFixture.away);
      }
      const base = teamsForThisWeek.size > 0
        ? Object.fromEntries(
            Object.entries(existing).filter(([pid]) => !teamsForThisWeek.has(PLAYER_IPL_TEAM[pid] || ""))
          )
        : existing;
      const merged = { ...base, ...wStats };
      const mergedPoints = {};
      for (const [pid, stat] of Object.entries(merged)) {
        const nm = {
          bat:  { runs: n(stat.bat?.runs), balls: n(stat.bat?.balls), fours: n(stat.bat?.fours), sixes: n(stat.bat?.sixes), out: !!stat.bat?.out },
          bowl: { overs: n(stat.bowl?.overs), runs: n(stat.bowl?.runs), wkts: n(stat.bowl?.wkts), maidens: n(stat.bowl?.maidens), dots: n(stat.bowl?.dots) },
          fld:  { catches: n(stat.fld?.catches), stumpings: n(stat.fld?.stumpings), runoutDirect: n(stat.fld?.runoutDirect), runoutAssist: n(stat.fld?.runoutAssist) },
        };
        mergedPoints[pid] = calcPlayerPoints(nm, DEFAULT_RULES);
      }
      dbUpdates[`leagues/${code}/season/stats/${w}`] = merged;
      dbUpdates[`leagues/${code}/season/playerPoints/${w}`] = mergedPoints;

      // Recompute team totals for this week from lineups
      const lineupsSnap = await get(ref(db, `leagues/${code}/season/lineups/${w}`));
      const lineups = lineupsSnap.val() || {};
      const teamTotals = {};
      for (const [teamName, ld] of Object.entries(lineups)) {
        const starters  = (ld?.starters || []).map(String);
        const captainId = ld?.captainId ? String(ld.captainId) : null;
        const vcId      = ld?.vcId      ? String(ld.vcId)      : null;
        let total = 0;
        for (const pid of starters) {
          const base = mergedPoints[pid] || 0;
          const mult = pid === captainId ? 2 : pid === vcId ? 1.5 : 1;
          total += base * mult;
        }
        teamTotals[teamName] = Math.round(total);
      }
      dbUpdates[`leagues/${code}/season/teamPoints/${w}`] = teamTotals;

      // Write matchup scores for this week
      const schedSnap = await get(ref(db, `leagues/${code}/season/schedule/${w}`));
      const schedWeek = schedSnap.val();
      if (schedWeek?.matchups) {
        dbUpdates[`leagues/${code}/season/schedule/${w}/matchups`] = schedWeek.matchups.map(m => ({
          ...m,
          homePoints: teamTotals[m.home] ?? m.homePoints ?? null,
          awayPoints: teamTotals[m.away] ?? m.awayPoints ?? null,
        }));
      }
    }

    await update(ref(db), dbUpdates);
    setLastPoll(new Date());
  }, [leagueCode, week, iplFixture]);

  // Debounced auto-save on every stat change
  const debouncedPush = useDebounce(pushAllScores, 800);

  // Trigger debounced save whenever stats change
  const statsRef = useRef(stats);
  statsRef.current = stats;
  useEffect(() => {
    if (Object.keys(stats).length === 0) return;
    debouncedPush(stats);
  }, [stats]); // eslint-disable-line

  // ── Manual save button ─────────────────────────────────────────
  const saveAllScores = async () => {
    setSaving(true);
    setStatus("Saving…");
    try {
      await pushAllScores(stats);
      setStatus(`✓ Saved! ${Object.keys(stats).length} players.`);
    } catch (e) {
      setStatus("Save failed: " + (e?.message || "unknown"));
    } finally {
      setSaving(false);
    }
  };

  // ── Live API ───────────────────────────────────────────────────
  const saveApiKey = () => {
    const k = apiKeyInput.trim();
    localStorage.setItem("cricket_api_key", k);
    setApiKey(k);
    setApiKeyInput("");
    setStatus("API key saved.");
  };

  // Inject API key into env simulation
  useEffect(() => {
    if (apiKey) window.__CRICKET_API_KEY__ = apiKey;
  }, [apiKey]);

  const startPolling = () => {
    if (!selectedMatch) { setStatus("Select a match first."); return; }
    if (pollerRef.current) pollerRef.current.stop();

    setPolling(true);
    setApiStatus("pulling");
    setPollError("");

    const poller = new LiveScorePoller({
      onMatchList: (matches) => setLiveMatches(matches),
      onStats: (apiStats) => {
        // Match API player names to our roster
        const updatedStats = { ...statsRef.current };
        let matched = 0;
        for (const [apiName, stat] of Object.entries(apiStats)) {
          const player = matchPlayerName(apiName, allPlayers);
          if (!player) continue;
          matched++;
          const pid = String(player.id);
          const incoming = {
            bat:  { runs: stat.bat.runs, balls: stat.bat.balls, fours: stat.bat.fours, sixes: stat.bat.sixes, out: stat.bat.out },
            bowl: { overs: stat.bowl.overs, runs: stat.bowl.runs, wkts: stat.bowl.wkts, maidens: stat.bowl.maidens, dots: stat.bowl.dots },
            fld:  { catches: stat.fld.catches, stumpings: stat.fld.stumpings, runoutDirect: stat.fld.runoutDirect, runoutAssist: stat.fld.runoutAssist },
          };
          updatedStats[pid] = updatedStats[pid] ? mergeMatchStat(updatedStats[pid], incoming) : incoming;
        }
        setStats(updatedStats);
        setApiStatus("live");
        setStatus(`✓ API: ${matched} players matched from live scorecard.`);
        setPollError("");
      },
      onError: (err) => {
        setApiStatus("error");
        setPollError(err?.message || "API error");
      },
    });

    // Override fetch to use stored key
    poller._apiKey = apiKey;
    poller.setMatch(selectedMatch);
    poller.start();
    pollerRef.current = poller;
  };

  const stopPolling = () => {
    pollerRef.current?.stop();
    setPolling(false);
    setApiStatus("");
  };

  // One-time fetch for completed (or live) matches — no polling needed
  const fetchOnce = useCallback(async () => {
    const mid = selectedMatch || manualMatchId.trim();
    if (!mid) { setStatus("Select a match or enter a Cricbuzz match ID first."); return; }
    // Fixture is required: without it, auto-save would write all players to the
    // currently selected manual week instead of each team's correct week.
    if (!iplMatchNum) {
      setStatus("⚠ Select the IPL Match number (top-left dropdown) first — required to route each team's stats to the correct fantasy week.");
      return;
    }
    setApiStatus("pulling");
    setPollError("");
    try {
      const sc = await fetchScorecard(mid);
      const apiStats = parseScorecardToStats(sc);
      const updatedStats = { ...statsRef.current };
      let matched = 0;
      for (const [apiName, stat] of Object.entries(apiStats)) {
        const player = matchPlayerName(apiName, allPlayers);
        if (!player) continue;
        matched++;
        const pid = String(player.id);
        const incoming = {
          bat:  { runs: stat.bat.runs, balls: stat.bat.balls, fours: stat.bat.fours, sixes: stat.bat.sixes, out: stat.bat.out },
          bowl: { overs: stat.bowl.overs, runs: stat.bowl.runs, wkts: stat.bowl.wkts, maidens: stat.bowl.maidens, dots: stat.bowl.dots },
          fld:  { catches: stat.fld.catches, stumpings: stat.fld.stumpings, runoutDirect: stat.fld.runoutDirect, runoutAssist: stat.fld.runoutAssist },
        };
        updatedStats[pid] = updatedStats[pid] ? mergeMatchStat(updatedStats[pid], incoming) : incoming;
      }
      setStats(updatedStats);
      setApiStatus("live");
      setLastPoll(new Date());
      const rawCount = Object.keys(apiStats).length;
      if (rawCount === 0) {
        setStatus("⚠ Scorecard returned no player data. The match may not have started, or the API response format is unrecognised.");
      } else {
        setStatus(`✓ Fetched: ${matched}/${rawCount} players matched from scorecard.${matched === 0 ? " Check browser console for unmatched names." : ""}`);
        // Log unmatched names to help debug
        if (matched < rawCount) {
          const unmatched = Object.keys(apiStats).filter(n => !matchPlayerName(n, allPlayers));
          console.info("Unmatched scorecard names:", unmatched);
        }
      }
    } catch (e) {
      setApiStatus("error");
      setPollError(e?.message || "API error");
    }
  }, [selectedMatch, manualMatchId, allPlayers, iplMatchNum]);

  // ── Shared helper: fetch + parse scorecard stats for a known fixture ─
  // Validates that the scorecard's players belong to the expected teams —
  // catches the common error of entering the wrong Cricbuzz ID for a slot.
  const syncOneFixture = useCallback(async (fixture, cricbuzzId) => {
    const sc       = await fetchScorecard(cricbuzzId);
    const apiStats = parseScorecardToStats(sc);
    if (!Object.keys(apiStats).length) {
      throw new Error("No player data returned — match may not be complete yet");
    }
    const matchStats = {};
    const teamsFound = new Set();
    for (const [apiName, stat] of Object.entries(apiStats)) {
      const player = matchPlayerName(apiName, allPlayers);
      if (!player) continue;
      const pid = String(player.id);
      const t = PLAYER_IPL_TEAM[pid] || "";
      if (t) teamsFound.add(t);
      const incoming = {
        bat:  { runs: stat.bat.runs,  balls: stat.bat.balls,  fours: stat.bat.fours,  sixes: stat.bat.sixes,  out: stat.bat.out  },
        bowl: { overs: stat.bowl.overs, runs: stat.bowl.runs, wkts: stat.bowl.wkts, maidens: stat.bowl.maidens, dots: stat.bowl.dots },
        fld:  { catches: stat.fld.catches, stumpings: stat.fld.stumpings, runoutDirect: stat.fld.runoutDirect, runoutAssist: stat.fld.runoutAssist },
      };
      matchStats[pid] = matchStats[pid] ? mergeMatchStat(matchStats[pid], incoming) : incoming;
    }
    // Validate the scorecard belongs to this fixture.
    // Require ≥2 matched players from EACH of the two expected teams.
    // A single overlapping team (e.g. SRH appears in both M17 and M21) is not
    // sufficient — the wrong scorecard still passes if we only check one team.
    const expected = new Set([fixture.home, fixture.away]);
    const perTeam = {};
    for (const pid of Object.keys(matchStats)) {
      const t = PLAYER_IPL_TEAM[pid] || "";
      if (expected.has(t)) perTeam[t] = (perTeam[t] || 0) + 1;
    }
    const bothTeamsPresent = [...expected].every(t => (perTeam[t] || 0) >= 2);
    if (!bothTeamsPresent && Object.keys(matchStats).length > 0) {
      const foundSummary = [...expected].map(t => `${t}:${perTeam[t] || 0}`).join(", ");
      throw new Error(`Wrong scorecard for M${fixture.match} (${fixture.home} vs ${fixture.away}): need ≥2 players per team, got [${foundSummary}]. Check Cricbuzz ID.`);
    }
    if (!Object.keys(matchStats).length) {
      throw new Error("0 players matched from scorecard");
    }
    await pushAllScores(matchStats, fixture);
  }, [allPlayers, pushAllScores]);

  // ── Bulk sync: paste any list of Cricbuzz IDs → auto-detect fixture ─
  // Fetches each scorecard, identifies the two teams from matched players,
  // finds the correct fixture automatically — no risk of wrong-slot errors.
  const bulkSync = useCallback(async () => {
    if (!leagueCode) return;
    const code = leagueCode.toUpperCase();
    const ids = [...new Set(
      bulkIdText.split(/[\n,\s;]+/).map(s => s.trim()).filter(s => /^\d{5,7}$/.test(s))
    )];
    if (!ids.length) {
      setSyncProgress({ done: 0, total: 0, msg: "⚠ No valid IDs found — paste 5-7 digit Cricbuzz match IDs." });
      return;
    }
    setSyncProgress({ done: 0, total: ids.length, msg: `Fetching ${ids.length} scorecards…` });
    setBulkResults([]);

    const savedSnap = await get(ref(db, `leagues/${code}/config/matchIds`));
    const idMap = { ...(savedSnap.val() || {}) };

    // Pre-seed usedFixtureIndices from already-saved ID map so we don't double-book
    const usedFixtureIndices = new Set(
      Object.entries(idMap)
        .map(([matchNum]) => IPL_2026_MATCHES.findIndex(f => f.match === Number(matchNum)))
        .filter(i => i >= 0)
    );

    let synced = 0, skipped = 0, errors = 0;
    const results = [];

    for (let i = 0; i < ids.length; i++) {
      const cricbuzzId = ids[i];
      setSyncProgress({ done: i, total: ids.length, msg: `[${i + 1}/${ids.length}] Scorecard ${cricbuzzId}…` });
      try {
        const sc = await fetchScorecard(cricbuzzId);
        const apiStats = parseScorecardToStats(sc);
        if (!Object.keys(apiStats).length) {
          results.push({ id: cricbuzzId, status: "skip", msg: "No player data (not complete yet?)" });
          skipped++;
          await new Promise(r => setTimeout(r, 400));
          continue;
        }

        // Build stats + tally teams from matched players
        const matchStats = {};
        const teamCounts = {};
        for (const [apiName, stat] of Object.entries(apiStats)) {
          const player = matchPlayerName(apiName, allPlayers);
          if (!player) continue;
          const pid = String(player.id);
          const t = PLAYER_IPL_TEAM[pid] || "";
          // Only count each player once toward team detection (avoid double-counting
          // when the same player appears under two name forms in the scorecard).
          if (t && !matchStats[pid]) teamCounts[t] = (teamCounts[t] || 0) + 1;
          const incoming = {
            bat:  { runs: stat.bat.runs,  balls: stat.bat.balls,  fours: stat.bat.fours,  sixes: stat.bat.sixes,  out: stat.bat.out  },
            bowl: { overs: stat.bowl.overs, runs: stat.bowl.runs, wkts: stat.bowl.wkts, maidens: stat.bowl.maidens, dots: stat.bowl.dots },
            fld:  { catches: stat.fld.catches, stumpings: stat.fld.stumpings, runoutDirect: stat.fld.runoutDirect, runoutAssist: stat.fld.runoutAssist },
          };
          matchStats[pid] = matchStats[pid] ? mergeMatchStat(matchStats[pid], incoming) : incoming;
        }

        // Pick top-2 teams by matched player count
        const topTeams = Object.entries(teamCounts)
          .sort((a, b) => b[1] - a[1]).slice(0, 2).map(([t]) => t);

        if (topTeams.length < 2) {
          results.push({ id: cricbuzzId, status: "skip", msg: `Only ${topTeams.length} team detected (${topTeams.join(", ") || "none"}) — names may not match roster` });
          skipped++;
          await new Promise(r => setTimeout(r, 400));
          continue;
        }

        const [tA, tB] = topTeams;
        // Find the earliest unmatched fixture for this team pair
        const fixtureIdx = IPL_2026_MATCHES.findIndex((f, idx) =>
          !usedFixtureIndices.has(idx) &&
          ((f.home === tA && f.away === tB) || (f.home === tB && f.away === tA))
        );
        if (fixtureIdx < 0) {
          results.push({ id: cricbuzzId, status: "skip", msg: `No unmatched fixture for ${tA} vs ${tB}` });
          skipped++;
          await new Promise(r => setTimeout(r, 400));
          continue;
        }

        usedFixtureIndices.add(fixtureIdx);
        const fixture = IPL_2026_MATCHES[fixtureIdx];
        idMap[String(fixture.match)] = cricbuzzId;

        await pushAllScores(matchStats, fixture);
        synced++;
        results.push({ id: cricbuzzId, status: "ok",
          msg: `M${fixture.match}: ${fixture.home} vs ${fixture.away} · ${Object.keys(matchStats).length} players` });
      } catch (e) {
        errors++;
        results.push({ id: cricbuzzId, status: "error", msg: e.message });
      }
      await new Promise(r => setTimeout(r, 700));
    }

    // Persist all newly-discovered IDs in one write
    await update(ref(db), { [`leagues/${code}/config/matchIds`]: idMap });
    setBulkResults(results);
    setSyncProgress({ done: ids.length, total: ids.length,
      msg: `✓ Done — ${synced} synced, ${skipped} skipped, ${errors} errors` });
    setLastPoll(new Date());
  }, [leagueCode, bulkIdText, allPlayers, pushAllScores]);

  // ── Purge stale stats ─────────────────────────────────────────
  // Removes player entries from weeks where their IPL team has no synced
  // fixture in matchIdMap.  This fixes old-corruption data that survived
  // before the stale-data merge fix was added — e.g. Buttler's Week 4
  // entry when GT's Week 4 fixture hasn't been played / synced yet.
  //
  // Safety: if a team has NO synced fixtures at all (never appears in
  // matchIdMap) we leave their data alone — it may be manually entered.
  const purgeStaleStats = useCallback(async () => {
    if (!leagueCode) return 0;
    const code = leagueCode.toUpperCase();

    // Which fixtures have been synced?
    const savedSnap = await get(ref(db, `leagues/${code}/config/matchIds`));
    const idMap = savedSnap.val() || {};

    // Build: IPL team → Set<fantasy week> that have a COMPLETED synced fixture.
    // Only count fixtures whose scheduled time has passed — upcoming matches
    // have IDs in the map (from API discovery) but haven't been played yet,
    // so player data for those team-weeks shouldn't exist and stale entries
    // from old bad syncs must be purged.
    const nowPurge = Date.now();
    const syncedTeamWeeks = {};
    for (const matchNum of Object.keys(idMap)) {
      const fixture = IPL_2026_MATCHES.find(f => f.match === Number(matchNum));
      if (!fixture) continue;
      if (fixture.time >= nowPurge) continue; // not yet played — skip
      for (const [team, gameRound] of [[fixture.home, fixture.homeGame], [fixture.away, fixture.awayGame]]) {
        const w = gameRoundToFantasyWeek(gameRound);
        if (!syncedTeamWeeks[team]) syncedTeamWeeks[team] = new Set();
        syncedTeamWeeks[team].add(w);
      }
    }

    let removed = 0;
    const dbUpdates = {};

    for (let w = 1; w <= 12; w++) {
      const statsSnap = await get(ref(db, `leagues/${code}/season/stats/${w}`));
      const existing = statsSnap.val() || {};
      if (!Object.keys(existing).length) continue;

      const cleaned = {};
      for (const [pid, stat] of Object.entries(existing)) {
        const team = PLAYER_IPL_TEAM[pid] || "";
        // Keep if: unknown team, OR team has a synced fixture for this week,
        // OR team has never been synced at all (may be manually entered).
        const hasSyncedWeeks = team && syncedTeamWeeks[team];
        if (!hasSyncedWeeks || syncedTeamWeeks[team].has(w)) {
          cleaned[pid] = stat;
        } else {
          removed++;
        }
      }

      if (Object.keys(cleaned).length === Object.keys(existing).length) continue; // nothing changed

      // Recalculate points for the cleaned set
      const cleanedPoints = {};
      for (const [pid, stat] of Object.entries(cleaned)) {
        const nm = {
          bat:  { runs: n(stat.bat?.runs), balls: n(stat.bat?.balls), fours: n(stat.bat?.fours), sixes: n(stat.bat?.sixes), out: !!stat.bat?.out },
          bowl: { overs: n(stat.bowl?.overs), runs: n(stat.bowl?.runs), wkts: n(stat.bowl?.wkts), maidens: n(stat.bowl?.maidens), dots: n(stat.bowl?.dots) },
          fld:  { catches: n(stat.fld?.catches), stumpings: n(stat.fld?.stumpings), runoutDirect: n(stat.fld?.runoutDirect), runoutAssist: n(stat.fld?.runoutAssist) },
        };
        cleanedPoints[pid] = calcPlayerPoints(nm, DEFAULT_RULES);
      }

      dbUpdates[`leagues/${code}/season/stats/${w}`] = cleaned;
      dbUpdates[`leagues/${code}/season/playerPoints/${w}`] = cleanedPoints;

      // Recompute team totals for affected weeks
      const lineupsSnap = await get(ref(db, `leagues/${code}/season/lineups/${w}`));
      const lineups = lineupsSnap.val() || {};
      const teamTotals = {};
      for (const [teamName, ld] of Object.entries(lineups)) {
        const starters  = (ld?.starters || []).map(String);
        const captainId = ld?.captainId ? String(ld.captainId) : null;
        const vcId      = ld?.vcId      ? String(ld.vcId)      : null;
        let total = 0;
        for (const pid of starters) {
          const base = cleanedPoints[pid] || 0;
          const mult = pid === captainId ? 2 : pid === vcId ? 1.5 : 1;
          total += base * mult;
        }
        teamTotals[teamName] = Math.round(total);
      }
      dbUpdates[`leagues/${code}/season/teamPoints/${w}`] = teamTotals;

      // Update matchup scores for this week
      const schedSnap = await get(ref(db, `leagues/${code}/season/schedule/${w}`));
      const schedWeek = schedSnap.val();
      if (schedWeek?.matchups) {
        dbUpdates[`leagues/${code}/season/schedule/${w}/matchups`] = schedWeek.matchups.map(m => ({
          ...m,
          homePoints: teamTotals[m.home] ?? m.homePoints ?? null,
          awayPoints: teamTotals[m.away] ?? m.awayPoints ?? null,
        }));
      }
    }

    if (Object.keys(dbUpdates).length) {
      await update(ref(db), dbUpdates);
    }
    return removed;
  }, [leagueCode]);

  // ── Full-season sync ───────────────────────────────────────────
  // Strategy:
  //  1. Fetch live + recent from API and update the persisted ID map
  //  2. Walk only NEW completed fixtures (not already synced) — avoids
  //     re-processing old weeks which corrupts the current week's stats
  //  3. forceAll=true re-syncs everything (host "Force Resync" button)
  const syncFullSeason = useCallback(async (forceAll = false) => {
    if (!leagueCode) return;
    const code = leagueCode.toUpperCase();
    setSyncProgress({ done: 0, total: 0, msg: "Discovering match IDs from Cricbuzz API…" });
    setBulkResults([]);

    try {
      // Load latest saved IDs from Firebase (onValue may lag, so re-fetch)
      const savedSnap = await get(ref(db, `leagues/${code}/config/matchIds`));
      const savedIdMap = savedSnap.val() || {};
      const idMap = { ...savedIdMap };

      // Load which fixtures have already been successfully synced
      const syncedSnap = await get(ref(db, `leagues/${code}/config/syncedMatches`));
      const syncedMatches = { ...(syncedSnap.val() || {}) };

      // First-run initialization: if syncedMatches is empty but we already have
      // saved IDs, those fixtures were synced before this tracking was introduced.
      // Pre-populate so the cron never re-processes historical completed fixtures.
      if (!forceAll && Object.keys(syncedMatches).length === 0 && Object.keys(savedIdMap).length > 0) {
        Object.keys(savedIdMap).forEach(matchNum => { syncedMatches[matchNum] = -1; });
      }

      // Fetch API match list and fill in any gaps in the saved ID map.
      const apiMatches = await fetchAllIplMatches();

      // Validate saved IDs against the live API response.
      // If an ID appears in the API for DIFFERENT teams than its stored fixture,
      // the saved ID is stale or wrong (e.g. a duplicate from before the
      // knownCricbuzzIds guard was added) — evict it so discovery can re-assign.
      // If the ID isn't in the current API response at all (old completed match),
      // we trust the saved value and keep it.
      const apiMatchById = new Map(
        apiMatches.filter(m => m.matchId).map(m => [String(m.matchId), m])
      );
      for (const [matchNum, cricId] of Object.entries(idMap)) {
        const fixture = IPL_2026_MATCHES.find(f => f.match === Number(matchNum));
        if (!fixture) { delete idMap[matchNum]; continue; }
        const apiMatch = apiMatchById.get(String(cricId));
        if (apiMatch) {
          const t1 = apiMatch.team1?.teamSName || "";
          const t2 = apiMatch.team2?.teamSName || "";
          const teamsMatch = (t1 === fixture.home && t2 === fixture.away) ||
                             (t1 === fixture.away && t2 === fixture.home);
          if (!teamsMatch) {
            delete idMap[matchNum]; // API shows this ID for wrong teams → evict
          }
        }
      }

      // Pre-seed apiMappedIndices with fixtures still in validated idMap so the
      // discovery loop never overwrites a correct saved ID.
      // Also pre-seed knownCricbuzzIds so the same Cricbuzz ID is never
      // assigned to two different fixtures (handles teams that play twice).
      const apiMappedIndices = new Set(
        Object.keys(idMap)
          .map(matchNum => IPL_2026_MATCHES.findIndex(f => f.match === Number(matchNum)))
          .filter(i => i >= 0)
      );
      const knownCricbuzzIds = new Set(Object.values(idMap).map(String));
      for (const m of apiMatches) {
        if (!m.matchId) continue;
        if (knownCricbuzzIds.has(String(m.matchId))) continue; // already assigned to a fixture
        const t1 = m.team1?.teamSName || "", t2 = m.team2?.teamSName || "";
        const fixtureIdx = IPL_2026_MATCHES.findIndex((f, idx) =>
          !apiMappedIndices.has(idx) &&
          ((f.home === t1 && f.away === t2) || (f.home === t2 && f.away === t1))
        );
        if (fixtureIdx >= 0) {
          apiMappedIndices.add(fixtureIdx);
          knownCricbuzzIds.add(String(m.matchId));
          idMap[String(IPL_2026_MATCHES[fixtureIdx].match)] = String(m.matchId);
        }
      }

      // Persist the enriched ID map back to Firebase
      await update(ref(db), { [`leagues/${code}/config/matchIds`]: idMap });

      // Walk all fixtures whose scheduled time has passed
      const now = Date.now();
      const completed = IPL_2026_MATCHES.filter(f => f.time < now);
      const withId    = completed.filter(f => idMap[String(f.match)]);
      const missing   = completed.filter(f => !idMap[String(f.match)]);

      // Skip fixtures already synced (unless force-resync requested)
      const toSync = forceAll
        ? withId
        : withId.filter(f => !syncedMatches[String(f.match)]);
      const skippedCount = withId.length - toSync.length;

      if (!toSync.length && !forceAll) {
        const msg = withId.length > 0
          ? `✓ All ${withId.length} fixtures already synced.${missing.length ? ` ${missing.length} have no ID.` : ""} Use Force Resync to re-process.`
          : `⚠ No Cricbuzz IDs found for any of the ${completed.length} completed fixtures.`;
        setSyncProgress({ done: 0, total: 0, msg });
        return;
      }

      setSyncProgress({ done: 0, total: toSync.length, msg: `Syncing ${toSync.length} fixture${toSync.length !== 1 ? "s" : ""}${skippedCount ? ` (${skippedCount} already up to date)` : ""}…` });

      let synced = 0, errors = 0;
      const results = [];

      for (let i = 0; i < toSync.length; i++) {
        const fixture = toSync[i];
        const cricbuzzId = idMap[String(fixture.match)];
        setSyncProgress({ done: i, total: toSync.length,
          msg: `[${i + 1}/${toSync.length}] M${fixture.match}: ${fixture.home} vs ${fixture.away}` });
        try {
          await syncOneFixture(fixture, cricbuzzId);
          synced++;
          syncedMatches[String(fixture.match)] = Date.now(); // mark as successfully synced
          results.push({ id: cricbuzzId, status: "ok",
            msg: `M${fixture.match}: ${fixture.home} vs ${fixture.away}` });
        } catch (e) {
          errors++;
          results.push({ id: cricbuzzId, status: "error",
            msg: `M${fixture.match} (${fixture.home} vs ${fixture.away}): ${e.message}` });
        }
        await new Promise(r => setTimeout(r, 700));
      }

      // Persist which fixtures are now successfully synced
      await update(ref(db), { [`leagues/${code}/config/syncedMatches`]: syncedMatches });

      if (missing.length) {
        results.push({ id: "—", status: "skip",
          msg: `${missing.length} fixture${missing.length > 1 ? "s" : ""} have no ID yet: ${missing.map(f => `M${f.match}`).join(", ")} — paste below` });
      }

      // Purge any stale player entries left over from old bad syncs
      setSyncProgress({ done: toSync.length, total: toSync.length, msg: "Cleaning up stale data…" });
      let purged = 0;
      try { purged = await purgeStaleStats(); } catch (_) {}

      setBulkResults(results);
      setSyncProgress({ done: toSync.length, total: toSync.length,
        msg: `✓ Sync complete — ${synced} synced${skippedCount ? `, ${skippedCount} skipped (up to date)` : ""}${errors ? `, ${errors} errors` : ""}${missing.length ? ` · ${missing.length} missing IDs` : ""}${purged > 0 ? ` · ${purged} stale entries removed` : ""}` });
      setLastPoll(new Date());
    } catch (e) {
      setSyncProgress(p => ({ ...(p || {}), msg: `✗ Sync failed: ${e.message}` }));
    }
  }, [leagueCode, syncOneFixture, purgeStaleStats]);

  useEffect(() => () => pollerRef.current?.stop(), []);

  // Fetch match list on mount if key is set
  useEffect(() => {
    if (!apiKey || !isHost) return;
    const poller = new LiveScorePoller({
      onMatchList: setLiveMatches,
      onError: () => {},
    });
    poller.start();
    pollerRef.current = poller;
    return () => poller.stop();
  }, [apiKey, isHost]); // eslint-disable-line

  // Auto-detect IPL fixture when a Cricbuzz match is selected from the dropdown.
  // Only fires when iplMatchNum is currently empty to avoid overriding manual selections.
  useEffect(() => {
    if (!selectedMatch || !liveMatches.length || iplMatchNum) return;
    const m = liveMatches.find(lm => String(lm.matchId) === String(selectedMatch));
    if (!m) return;
    const t1 = m.team1?.teamSName || "";
    const t2 = m.team2?.teamSName || "";
    const fixture = IPL_2026_MATCHES.find(f =>
      (f.home === t1 && f.away === t2) || (f.home === t2 && f.away === t1)
    );
    if (fixture) setIplMatchNum(String(fixture.match));
  }, [selectedMatch, liveMatches]); // eslint-disable-line

  const computePoints = (pid) => {
    const stat = stats[String(pid)];
    if (!stat) return null;
    const nm = {
      bat:  { runs: n(stat.bat?.runs), balls: n(stat.bat?.balls), fours: n(stat.bat?.fours), sixes: n(stat.bat?.sixes), out: !!stat.bat?.out },
      bowl: { overs: n(stat.bowl?.overs), runs: n(stat.bowl?.runs), wkts: n(stat.bowl?.wkts), maidens: n(stat.bowl?.maidens), dots: n(stat.bowl?.dots) },
      fld:  { catches: n(stat.fld?.catches), stumpings: n(stat.fld?.stumpings), runoutDirect: n(stat.fld?.runoutDirect), runoutAssist: n(stat.fld?.runoutAssist) },
    };
    return calcPlayerPoints(nm, DEFAULT_RULES);
  };

  if (!isHost) {
    return (
      <div className="panel" style={{ padding: 24, textAlign: "center" }}>
        <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 8 }}>Score Entry</div>
        <div style={{ color: "var(--text-dim)" }}>Only the league host can enter scores.</div>
      </div>
    );
  }

  const hasKey = !!apiKey;

  // Reusable progress bar + message for sync
  const SyncStatus = syncProgress ? (
    <div>
      {syncProgress.total > 0 && (
        <div style={{ height: 4, background: "var(--border2)", borderRadius: 2, marginBottom: 6, overflow: "hidden" }}>
          <div style={{
            height: "100%", borderRadius: 2, transition: "width 0.3s",
            background: syncProgress.msg?.startsWith("✓") ? "var(--green)" : "var(--gold)",
            width: `${Math.round((syncProgress.done / syncProgress.total) * 100)}%`,
          }} />
        </div>
      )}
      <div style={{
        fontSize: 12, padding: "6px 10px", borderRadius: "var(--radius-sm)", border: "1px solid",
        background:    syncProgress.msg?.startsWith("✓") ? "rgba(74,222,128,0.08)"  : syncProgress.msg?.startsWith("✗") || syncProgress.msg?.startsWith("⚠") ? "rgba(248,113,113,0.08)"  : "rgba(245,166,35,0.08)",
        color:         syncProgress.msg?.startsWith("✓") ? "var(--green)"            : syncProgress.msg?.startsWith("✗") || syncProgress.msg?.startsWith("⚠") ? "var(--red)"               : "var(--gold)",
        borderColor:   syncProgress.msg?.startsWith("✓") ? "rgba(74,222,128,0.2)"   : syncProgress.msg?.startsWith("✗") || syncProgress.msg?.startsWith("⚠") ? "rgba(248,113,113,0.2)"   : "rgba(245,166,35,0.2)",
      }}>
        {syncProgress.total > 0 && `${syncProgress.done}/${syncProgress.total} · `}{syncProgress.msg}
      </div>
      {bulkResults.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 2, marginTop: 6,
          maxHeight: 180, overflowY: "auto", fontSize: 11,
          background: "var(--bg2)", borderRadius: "var(--radius-sm)",
          padding: "6px 10px", border: "1px solid var(--border)" }}>
          {bulkResults.map((r, i) => (
            <div key={i} style={{ display: "flex", gap: 8 }}>
              <span style={{ color: "var(--text-muted)", minWidth: 54, fontFamily: "monospace", flexShrink: 0 }}>{r.id}</span>
              <span style={{ color: r.status === "ok" ? "var(--green)" : r.status === "error" ? "var(--red)" : "var(--gold)" }}>
                {r.status === "ok" ? "✓" : r.status === "error" ? "✗" : "–"} {r.msg}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  ) : null;

  return (
    <div>
      {/* ── Combined header + action panel ── */}
      <div className="panel" style={{ marginBottom: 12 }}>
        <div className="panel-head">
          <div>
            <div style={{ fontWeight: 800, fontSize: 18, fontFamily: "'Playfair Display', Georgia, serif" }}>
              Score Entry
              {apiStatus === "live" && <span style={{ marginLeft: 10, fontSize: 11, color: "var(--green)", fontFamily: "sans-serif", fontWeight: 700 }}>● LIVE</span>}
              {apiStatus === "pulling" && <span style={{ marginLeft: 10, fontSize: 11, color: "var(--gold)", fontFamily: "sans-serif" }}>● Connecting…</span>}
            </div>
            <div style={{ color: "var(--text-dim)", fontSize: 13, marginTop: 2 }}>
              {lastPoll ? `Updated ${lastPoll.toLocaleTimeString()}` : "Stats auto-save as you type"}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="primary" onClick={saveAllScores} disabled={saving}>
              {saving ? "Saving…" : "Save Now"}
            </button>
          </div>
        </div>

        <div style={{ padding: 14, borderTop: "1px solid var(--border)", display: "flex", flexDirection: "column", gap: 10 }}>
          {hasKey ? (
            <>
              {/* ── Primary action: pick match → fetch ── */}
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <select
                  value={selectedMatch}
                  onChange={e => { setSelectedMatch(e.target.value); setManualMatchId(""); }}
                  style={{ flex: 1, minWidth: 220 }}
                >
                  <option value="">— Select match —</option>
                  {liveMatches.filter(m => m.matchState === "live").length > 0 && (
                    <optgroup label="🔴 Live">
                      {liveMatches.filter(m => m.matchState === "live").map(m => (
                        <option key={m.matchId} value={m.matchId}>
                          {m.team1?.teamSName} vs {m.team2?.teamSName}{m.status ? ` · ${m.status}` : ""}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  {liveMatches.filter(m => m.matchState === "recent").length > 0 && (
                    <optgroup label="✓ Recently Completed">
                      {liveMatches.filter(m => m.matchState === "recent").map(m => (
                        <option key={m.matchId} value={m.matchId}>
                          {m.team1?.teamSName} vs {m.team2?.teamSName}{m.status ? ` · ${m.status}` : ""}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  {liveMatches.length === 0 && <option disabled value="">Loading matches…</option>}
                </select>

                <button className="primary" onClick={fetchOnce}
                  disabled={(!selectedMatch && !manualMatchId.trim()) || !iplMatchNum} type="button">
                  ⬇ Fetch &amp; Save
                </button>

                {!polling ? (
                  <button className="secondary" onClick={startPolling} disabled={!selectedMatch || !iplMatchNum}
                    type="button" title="Auto-refresh every 30s — use for live matches">
                    ▶ Live Poll
                  </button>
                ) : (
                  <button className="danger" onClick={stopPolling} type="button">■ Stop</button>
                )}

                <button className="secondary" onClick={() => syncFullSeason(false)}
                  disabled={!liveMatches.length || syncProgress?.msg?.startsWith("[")}
                  type="button" style={{ whiteSpace: "nowrap" }}
                  title="Fetch scorecards for new/unsynced fixtures only">
                  ⟳ Sync New
                </button>
                <button className="secondary" onClick={() => syncFullSeason(true)}
                  disabled={!liveMatches.length || syncProgress?.msg?.startsWith("[")}
                  type="button" style={{ whiteSpace: "nowrap", color: "var(--red)", borderColor: "rgba(248,113,113,0.4)" }}
                  title="Re-sync ALL completed fixtures from scratch (clears and re-applies everything)">
                  ⟳ Force Resync All
                </button>
              </div>

              {/* Week routing info — shown once a fixture is auto-detected */}
              {iplFixture && (() => {
                const hw = gameRoundToFantasyWeek(iplFixture.homeGame);
                const aw = gameRoundToFantasyWeek(iplFixture.awayGame);
                const isCrossWeek = hw !== aw;
                return (
                  <div style={{
                    fontSize: 12, padding: "5px 10px", borderRadius: "var(--radius-sm)",
                    background: isCrossWeek ? "rgba(245,166,35,0.12)" : "rgba(245,166,35,0.07)",
                    border: `1px solid ${isCrossWeek ? "rgba(245,166,35,0.4)" : "rgba(245,166,35,0.2)"}`,
                    color: "var(--text-dim)", display: "flex", alignItems: "center", gap: 8,
                  }}>
                    {isCrossWeek && <span style={{ fontWeight: 700, color: "var(--gold)" }}>Cross-week:</span>}
                    <span>M{iplFixture.match} · {iplFixture.home} → <strong style={{ color: "var(--gold)" }}>Wk {hw}</strong></span>
                    <span style={{ opacity: 0.3 }}>·</span>
                    <span>{iplFixture.away} → <strong style={{ color: "var(--gold)" }}>Wk {aw}</strong></span>
                    <button className="mini" onClick={() => { setIplMatchNum(""); setSelectedMatch(""); setStats({}); setStatus(""); }}
                      type="button" style={{ marginLeft: "auto", fontSize: 10 }}>✕ Clear</button>
                  </div>
                );
              })()}

              {/* No fixture auto-detected yet but a Cricbuzz match is chosen */}
              {(selectedMatch || manualMatchId.trim()) && !iplFixture && (
                <div style={{ fontSize: 12, color: "var(--gold)" }}>
                  ⚠ Couldn't match this to a fixture automatically — use the IPL Match selector below.
                </div>
              )}

              {/* Sync progress */}
              {SyncStatus}

              {/* Error */}
              {pollError && (
                <div style={{ fontSize: 12, color: "var(--red)", padding: "6px 10px",
                  background: "rgba(248,113,113,0.08)", borderRadius: "var(--radius-sm)", border: "1px solid rgba(248,113,113,0.2)" }}>
                  ⚠ {pollError}
                </div>
              )}

              {/* Advanced: older match ID + manual fixture override + key removal */}
              <details style={{ fontSize: 12 }}>
                <summary style={{ cursor: "pointer", color: "var(--text-dim)", userSelect: "none" }}>Advanced</summary>
                <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8 }}>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <input className="search-box" value={manualMatchId}
                      onChange={e => { setManualMatchId(e.target.value); setSelectedMatch(""); }}
                      placeholder="Paste Cricbuzz match ID (for older matches)…"
                      style={{ flex: 1 }} />
                  </div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <span className="label">IPL Match</span>
                    <select value={iplMatchNum} onChange={e => { setIplMatchNum(e.target.value); setStats({}); setStatus(""); }} style={{ flex: 1 }}>
                      <option value="">— auto-detect —</option>
                      {IPL_2026_MATCHES.map(m => (
                        <option key={m.match} value={m.match}>
                          M{m.match}: {m.home} vs {m.away} · {m.date}
                        </option>
                      ))}
                    </select>
                    <button className="mini danger" onClick={() => { localStorage.removeItem("cricket_api_key"); setApiKey(""); }} type="button">
                      Remove Key
                    </button>
                  </div>
                </div>
              </details>
            </>
          ) : (
            <>
              {/* ── No API key: setup + manual selectors ── */}
              <div style={{ fontSize: 13, color: "var(--text-dim)", lineHeight: 1.6 }}>
                Connect to the <strong style={{ color: "var(--text)" }}>Cricbuzz Cricket API</strong> via RapidAPI
                to fetch scorecards automatically and use <strong>Sync All</strong>.
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                <input className="search-box" value={apiKeyInput} onChange={e => setApiKeyInput(e.target.value)}
                  placeholder="Paste your RapidAPI key…" type="password" style={{ flex: 1, minWidth: 200 }} />
                <button className="primary" onClick={saveApiKey} disabled={!apiKeyInput.trim()} type="button">Save Key</button>
              </div>
              <div style={{ fontSize: 11, color: "var(--text-muted)" }}>
                Get a free key at rapidapi.com → search "Cricbuzz Cricket API" → Basic plan. Stored in your browser only.
              </div>
              {/* Manual week/match selector for no-API entry */}
              <div style={{ borderTop: "1px solid var(--border)", paddingTop: 10, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <span className="label">IPL Match</span>
                <select value={iplMatchNum} onChange={e => { setIplMatchNum(e.target.value); setStats({}); setStatus(""); }}>
                  <option value="">— manual week —</option>
                  {IPL_2026_MATCHES.map(m => (
                    <option key={m.match} value={m.match}>M{m.match}: {m.home} vs {m.away} · {m.date}</option>
                  ))}
                </select>
                {!iplMatchNum && (
                  <>
                    <span className="label">Week</span>
                    <select value={week} onChange={e => setWeek(Number(e.target.value))}>
                      {Array.from({ length: 12 }, (_, i) => i + 1).map(w => (
                        <option key={w} value={w}>Week {w}</option>
                      ))}
                    </select>
                  </>
                )}
                {iplFixture && (() => {
                  const hw = gameRoundToFantasyWeek(iplFixture.homeGame);
                  const aw = gameRoundToFantasyWeek(iplFixture.awayGame);
                  return (
                    <span style={{ fontSize: 12, color: "var(--text-dim)" }}>
                      {iplFixture.home} → <strong style={{ color: "var(--gold)" }}>Wk {hw}</strong>
                      {hw !== aw && <> · {iplFixture.away} → <strong style={{ color: "var(--gold)" }}>Wk {aw}</strong></>}
                    </span>
                  );
                })()}
              </div>
              {/* Sync All disabled until key added */}
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <button className="secondary" disabled type="button" style={{ whiteSpace: "nowrap" }}>⟳ Sync All</button>
                <span style={{ fontSize: 11, color: "var(--text-dim)" }}>Add API key to enable</span>
              </div>
              {SyncStatus}
            </>
          )}
        </div>

        {status && (
          <div style={{ padding: "8px 16px", fontSize: 13, borderTop: "1px solid var(--border)",
            color: status.startsWith("✓") ? "var(--green)" : status.startsWith("⚠") ? "var(--gold)" : "var(--text)" }}>
            {status}
          </div>
        )}
      </div>

      {/* ── Bulk sync panel ── */}
      {hasKey && (
        <div className="panel" style={{ marginBottom: 12 }}>
          <div className="panel-head">
            <div style={{ fontWeight: 700, fontSize: 14 }}>Sync Past Matches</div>
            <div style={{ fontSize: 11, color: "var(--text-dim)" }}>
              {Object.keys(matchIdMap).length} fixture{Object.keys(matchIdMap).length !== 1 ? "s" : ""} saved ·{" "}
              {IPL_2026_MATCHES.filter(f => f.time < Date.now() && matchIdMap[String(f.match)]).length} synced
            </div>
          </div>
          <div style={{ padding: 14, display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ fontSize: 12, color: "var(--text-dim)", lineHeight: 1.6 }}>
              Go to <strong style={{ color: "var(--text)" }}>cricbuzz.com</strong>, open each completed IPL match scorecard,
              and copy the number from the URL:
              <code style={{ marginLeft: 6, fontSize: 11, color: "var(--gold)" }}>/live-cricket-scorecard/<strong>149618</strong>/...</code>
              <br />
              Paste all the IDs below — the system auto-detects which fixture each scorecard belongs to.
              No need to match them to matches manually.
            </div>

            <textarea
              value={bulkIdText}
              onChange={e => setBulkIdText(e.target.value)}
              placeholder={"Paste IDs one per line (or space/comma separated):\n149618\n149612\n149605\n..."}
              rows={5}
              style={{
                fontFamily: "monospace", fontSize: 13, resize: "vertical",
                background: "var(--bg2)", border: "1px solid var(--border)",
                color: "var(--text)", borderRadius: "var(--radius-sm)", padding: "8px 10px",
              }}
            />

            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <button className="primary" onClick={bulkSync}
                disabled={!bulkIdText.trim() || syncProgress?.msg?.startsWith("[")} type="button">
                ⟳ Sync All IDs
              </button>
              {bulkIdText.trim() && (
                <span style={{ fontSize: 11, color: "var(--text-dim)" }}>
                  {[...new Set(bulkIdText.split(/[\n,\s;]+/).filter(s => /^\d{5,7}$/.test(s.trim())))].length} IDs detected
                </span>
              )}
              <button className="secondary" type="button"
                disabled={syncProgress?.msg?.startsWith("[")}
                title="Remove player stats from weeks where their IPL team has no synced fixture — fixes stale data from old bad syncs"
                onClick={async () => {
                  setSyncProgress({ done: 0, total: 0, msg: "Scanning for stale data…" });
                  setBulkResults([]);
                  try {
                    const removed = await purgeStaleStats();
                    setSyncProgress({ done: 1, total: 1,
                      msg: removed > 0
                        ? `✓ Removed ${removed} stale player entr${removed === 1 ? "y" : "ies"} from wrong weeks`
                        : "✓ Nothing to clean — all player data is in the correct weeks"
                    });
                  } catch (e) {
                    setSyncProgress({ done: 0, total: 0, msg: `✗ Cleanup failed: ${e.message}` });
                  }
                }}>
                ✦ Clean Up Stale Data
              </button>
            </div>

            {/* Per-ID results */}
            {bulkResults.length > 0 && (
              <div style={{ display: "flex", flexDirection: "column", gap: 3,
                maxHeight: 220, overflowY: "auto", fontSize: 11,
                background: "var(--bg2)", borderRadius: "var(--radius-sm)",
                padding: "8px 10px", border: "1px solid var(--border)" }}>
                {bulkResults.map((r, i) => (
                  <div key={i} style={{ display: "flex", gap: 8 }}>
                    <span style={{ color: "var(--text-muted)", minWidth: 54, fontFamily: "monospace" }}>{r.id}</span>
                    <span style={{ color: r.status === "ok" ? "var(--green)" : r.status === "error" ? "var(--red)" : "var(--gold)" }}>
                      {r.status === "ok" ? "✓" : r.status === "error" ? "✗" : "–"} {r.msg}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Manual stats table ── */}
      <div style={{ marginBottom: 8 }}>
        <input className="search-box" placeholder="Search player…" value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      {/* Column headers */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "170px 48px 48px 40px 40px 36px 48px 48px 48px 40px 48px 64px",
        gap: 5, padding: "6px 14px",
        fontSize: 9, fontWeight: 700, letterSpacing: "0.08em",
        color: "var(--text-muted)", textTransform: "uppercase",
        borderBottom: "2px solid var(--border2)",
      }}>
        <span>Player</span>
        <span style={{ textAlign: "center" }}>Runs</span>
        <span style={{ textAlign: "center" }}>Balls</span>
        <span style={{ textAlign: "center" }}>4s</span>
        <span style={{ textAlign: "center" }}>6s</span>
        <span style={{ textAlign: "center" }}>Out</span>
        <span style={{ textAlign: "center" }}>Ovrs</span>
        <span style={{ textAlign: "center" }}>RC</span>
        <span style={{ textAlign: "center" }}>Wkt</span>
        <span style={{ textAlign: "center" }}>Mdn</span>
        <span style={{ textAlign: "center" }}>Ct/St</span>
        <span style={{ textAlign: "right" }}>PTS</span>
      </div>

      <div className="panel">
        {filtered.map(player => {
          const stat = getStat(player.id);
          const pts  = computePoints(player.id);
          const hasData = Object.values(stats[String(player.id)] || {}).some(
            sec => Object.values(sec || {}).some(v => v !== "" && v !== false && v !== 0)
          );
          const fromApi = polling && hasData;

          // For cross-week matches, show which week this player saves to
          const playerIplTeam = PLAYER_IPL_TEAM[String(player.id)] || "";
          const playerWeek = iplFixture
            ? (playerIplTeam === iplFixture.home
                ? gameRoundToFantasyWeek(iplFixture.homeGame)
                : gameRoundToFantasyWeek(iplFixture.awayGame))
            : week;
          const isCrossWeekMatch = iplFixture &&
            gameRoundToFantasyWeek(iplFixture.homeGame) !== gameRoundToFantasyWeek(iplFixture.awayGame);

          return (
            <div key={player.id} style={{
              display: "grid",
              gridTemplateColumns: "170px 48px 48px 40px 40px 36px 48px 48px 48px 40px 48px 64px",
              gap: 5, padding: "6px 14px",
              borderBottom: "1px solid var(--border)",
              alignItems: "center",
              background: fromApi ? "rgba(74,222,128,0.03)" : hasData ? "rgba(245,166,35,0.03)" : "transparent",
            }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {player.name}
                  {fromApi && <span style={{ marginLeft: 5, fontSize: 8, color: "var(--green)", fontWeight: 700 }}>LIVE</span>}
                  {isCrossWeekMatch && (
                    <span style={{ marginLeft: 5, fontSize: 8, color: "var(--gold)", fontWeight: 700,
                      background: "rgba(245,166,35,0.12)", padding: "1px 4px", borderRadius: 3 }}>
                      W{playerWeek}
                    </span>
                  )}
                </div>
                <span className={`role-chip role-${player.role}`}>{player.role}</span>
              </div>

              <StatInput value={stat.bat.runs}    onChange={v => updateStat(player.id, "bat",  "runs",    v)} />
              <StatInput value={stat.bat.balls}   onChange={v => updateStat(player.id, "bat",  "balls",   v)} />
              <StatInput value={stat.bat.fours}   onChange={v => updateStat(player.id, "bat",  "fours",   v)} width={40} />
              <StatInput value={stat.bat.sixes}   onChange={v => updateStat(player.id, "bat",  "sixes",   v)} width={40} />

              <div style={{ textAlign: "center" }}>
                <input type="checkbox" checked={!!stat.bat.out}
                  onChange={e => updateStat(player.id, "bat", "out", e.target.checked)}
                  style={{ width: 15, height: 15, accentColor: "var(--gold)", cursor: "pointer" }} />
              </div>

              <StatInput value={stat.bowl.overs}   onChange={v => updateStat(player.id, "bowl", "overs",   v)} />
              <StatInput value={stat.bowl.runs}    onChange={v => updateStat(player.id, "bowl", "runs",    v)} />
              <StatInput value={stat.bowl.wkts}    onChange={v => updateStat(player.id, "bowl", "wkts",    v)} width={48} />
              <StatInput value={stat.bowl.maidens} onChange={v => updateStat(player.id, "bowl", "maidens", v)} width={40} />

              <StatInput
                value={n(stat.fld.catches) + n(stat.fld.stumpings) || ""}
                onChange={v => updateStat(player.id, "fld", "catches", v)}
                width={48}
              />

              <div style={{
                textAlign: "right", fontWeight: 700, fontSize: 15,
                fontFamily: "'Playfair Display', Georgia, serif",
                color: pts === null ? "var(--text-muted)"
                     : pts >= 60   ? "var(--gold)"
                     : pts >= 30   ? "var(--green)"
                     : pts < 0     ? "var(--red)" : "var(--text)",
              }}>
                {pts === null ? "—" : pts}
              </div>
            </div>
          );
        })}

        {filtered.length === 0 && (
          <div style={{ padding: 24, textAlign: "center", color: "var(--text-dim)" }}>
            No players found. Complete the draft first.
          </div>
        )}
      </div>

      {/* Scoring reference */}
      <div className="panel" style={{ marginTop: 12, padding: 14 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12, fontSize: 11, color: "var(--text-dim)" }}>
          <div><div style={{ fontWeight: 700, color: "#93c5fd", marginBottom: 4 }}>BATTING</div>
            Run: +1 · 4: +1 · 6: +2<br />30+: +4 · 50+: +8 · 75+: +12 · 100+: +16<br />Duck (out): −2</div>
          <div><div style={{ fontWeight: 700, color: "#86efac", marginBottom: 4 }}>BOWLING</div>
            Wicket: +25 · Maiden: +8<br />Dot: +1 · 2W: +4 · 3W: +8 · 4W: +12 · 5W: +16</div>
          <div><div style={{ fontWeight: 700, color: "#d8b4fe", marginBottom: 4 }}>FIELDING</div>
            Catch: +8 · Stumping: +12<br />Direct run-out: +12 · Assist: +6</div>
        </div>
      </div>
    </div>
  );
}
